import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SharePanel from '@/components/feature/SharePanel';
import Black5Panel from '@/pages/detail/components/Black5Panel';
import RelatedNews from '@/pages/detail/components/RelatedNews';
import { events, findEvent } from '@/mocks/events';
import { findCategory } from '@/mocks/categories';
import { analyses } from '@/mocks/analyses';
import { statusClass, statusText } from '@/utils/format';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function DetailPage() {
  const { eventId = '' } = useParams();
  const event = findEvent(eventId);
  const [shareOpen, setShareOpen] = useState(false);

  const category = event ? findCategory(event.categoryId) : undefined;
  const title = event ? event.title : '赛事详情';

  usePageMeta({
    title: `【${title}】详情 - 51每日大赛`,
    description: event
      ? `${event.title}，${event.league}，比赛时间 ${event.startTime}，地点 ${event.location}。黑5综合指数 ${event.black5Index.toFixed(1)}，含关键指标对比与往期数据走势。`
      : '51每日大赛赛事详情页，查看赛事简介、黑5数据详情与分析内容。',
    keywords: event
      ? `${event.title},${event.homeTeam},${event.awayTeam},黑5数据,赛事详情,51每日大赛`
      : '赛事详情,51每日大赛',
  });

  if (!event) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mx-auto">
          <i className="ri-error-warning-line w-7 h-7 flex items-center justify-center text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该赛事</h1>
        <p className="mt-2 text-sm text-foreground-600">该赛事可能已被移除，或链接不正确。</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Link
            to="/"
            className="px-5 py-2.5 rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-800 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
          <Link
            to="/category/football"
            className="px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 transition-colors whitespace-nowrap"
          >
            浏览赛事分类
          </Link>
        </div>
      </div>
    );
  }

  const relatedEvents = events
    .filter((item) => item.categoryId === event.categoryId && item.id !== event.id)
    .slice(0, 3);
  const relatedAnalyses = analyses.filter((item) => item.categoryId === event.categoryId).slice(0, 2);
  const scoreText =
    event.status === 'upcoming' ? 'VS' : `${event.homeScore ?? 0} : ${event.awayScore ?? 0}`;

  return (
    <div className="w-full px-4 md:px-6 pt-6 md:pt-8 pb-10 md:pb-12">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: category?.name ?? '赛事分类', to: `/category/${event.categoryId}` },
          { label: event.homeTeam + ' vs ' + event.awayTeam },
        ]}
      />

      <div className="mt-4 flex flex-col lg:flex-row gap-6 md:gap-8">
        <div className="flex-1 min-w-0 flex flex-col gap-5 md:gap-6">
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="relative w-full h-56 md:h-80 bg-background-200">
              <img
                src={event.cover}
                alt={event.title}
                title={`${event.title} 赛事主图`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs ${statusClass(event.status)}`}>
                    {statusText(event.status)}
                  </span>
                  <span className="text-xs text-background-100/85">{event.league}</span>
                </div>
                <h1 className="font-heading text-xl md:text-3xl font-bold text-background-50 leading-tight">
                  {event.title}
                </h1>
              </div>
            </div>

            <div className="p-5 md:p-6">
              <div className="flex items-center justify-center gap-6 md:gap-12 py-4">
                <div className="text-center flex-1">
                  <span className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary-100 text-primary-800 flex items-center justify-center mx-auto font-heading font-bold text-lg">
                    {event.homeTeam.slice(0, 1)}
                  </span>
                  <p className="mt-3 text-sm md:text-base font-medium text-foreground-900">{event.homeTeam}</p>
                  <p className="text-xs text-foreground-500">主队</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-2xl md:text-4xl font-bold text-accent-600">{scoreText}</p>
                  <p className="mt-2 text-xs text-foreground-500">{event.startTime}</p>
                </div>
                <div className="text-center flex-1">
                  <span className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center mx-auto font-heading font-bold text-lg">
                    {event.awayTeam.slice(0, 1)}
                  </span>
                  <p className="mt-3 text-sm md:text-base font-medium text-foreground-900">{event.awayTeam}</p>
                  <p className="text-xs text-foreground-500">客队</p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-5 border-t border-background-200">
                <div className="flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center text-primary-600" />
                  {event.startTime}
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center text-primary-600" />
                  {event.location}
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center text-primary-600" />
                  {event.region} · {event.type}
                </div>
              </div>
            </div>
          </div>

          <section className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950 flex items-center gap-2 mb-3">
              <i className="ri-article-line text-primary-600 w-5 h-5 flex items-center justify-center" />
              比赛简介
            </h2>
            <p className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">{event.summary}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {event.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-xs bg-primary-100 text-primary-700 border border-primary-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          </section>

          <Black5Panel event={event} />

          <RelatedNews eventId={event.id} categoryId={event.categoryId} />

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/category/${event.categoryId}`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center" />
              返回{category?.name ?? '分类'}
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-5-line w-4 h-4 flex items-center justify-center" />
              返回首页
            </Link>
          </div>
        </div>

        <aside className="w-full lg:w-72 shrink-0">
          <div className="lg:sticky lg:top-24 flex flex-col gap-4">
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center" />
              分享这条赛事
            </button>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">赛事速览</h3>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">状态</span>
                  <span className="text-foreground-900">{statusText(event.status)}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">时间</span>
                  <span className="text-foreground-900">{event.startTime}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">地区</span>
                  <span className="text-foreground-900">{event.region}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">黑5指数</span>
                  <span className="font-heading font-semibold text-primary-700">
                    {event.black5Index.toFixed(1)}
                  </span>
                </li>
              </ul>
            </div>

            {relatedAnalyses.length > 0 ? (
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <h3 className="font-heading text-sm font-semibold text-foreground-950 px-4 py-3 border-b border-background-200">
                  相关数据分析
                </h3>
                <ul className="divide-y divide-background-200">
                  {relatedAnalyses.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/analysis/${item.id}`}
                        className="group block px-4 py-3 hover:bg-background-100/70 transition-colors"
                      >
                        <h4 className="text-sm text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                          {item.title}
                        </h4>
                        <span className="mt-1 inline-block text-[11px] text-foreground-500">
                          更新于 {item.updatedAt}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <h3 className="font-heading text-sm font-semibold text-foreground-950 px-4 py-3 border-b border-background-200">
                同类赛事
              </h3>
              <ul className="divide-y divide-background-200">
                {relatedEvents.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="group flex items-center gap-3 px-4 py-3 hover:bg-background-100/70 transition-colors"
                    >
                      <span className="w-14 h-12 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={item.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-xs text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                          {item.title}
                        </h4>
                        <span className="text-[11px] text-foreground-500">{item.startTime.slice(5)}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>

      <SharePanel title={event.title} open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}